import type { EventType, ScenarioTag } from '../../lib/decode/eventMarkers';

export type ScenarioPreset = {
  id: 'brake' | 'steering' | 'idle' | 'throttle_20' | 'door' | 'custom';
  title: string;
  shortLabel: string;
  scenarioTag: ScenarioTag;
  eventType: EventType;
  markerLabel: string;
  steps: string[];
  expected: string;
  markHint: string;
  notes: string;
  futureUse: string;
};

const SCENARIOS: ScenarioPreset[] = [
  {
    id: 'brake',
    title: 'Brake test',
    shortLabel: 'Brake',
    scenarioTag: 'braking',
    eventType: 'brake',
    markerLabel: 'Brake press',
    steps: ['Start logging while stationary or rolling slowly.', 'Press brake 3 times with clear 2-3 second pauses.', 'Mark the start of the first firm press, then run correlation.'],
    expected: 'Brake-related bytes should rise, fall, or flag shortly after the marker.',
    markHint: 'Mark the exact moment your foot begins pressing the pedal.',
    notes: 'Brake signals often appear as flags, pressure values, or pedal state bytes.',
    futureUse: 'Useful for comparing repeated brake sessions and training brake-signal matchers later.'
  },
  {
    id: 'steering',
    title: 'Steering sweep',
    shortLabel: 'Steering',
    scenarioTag: 'steering',
    eventType: 'steering',
    markerLabel: 'Steering movement',
    steps: ['Hold the vehicle stationary if safe.', 'Sweep the wheel left and right smoothly.', 'Mark the start of movement.'],
    expected: 'Steering angle bytes should move directionally and may cross through center.',
    markHint: 'Mark when the wheel first leaves center.',
    notes: 'Look for signed values or byte pairs that change smoothly with wheel motion.',
    futureUse: 'Useful for scenario comparison of steering angle candidates.'
  },
  {
    id: 'idle',
    title: 'Idle baseline',
    shortLabel: 'Idle',
    scenarioTag: 'idle',
    eventType: 'idle',
    markerLabel: 'Stable idle',
    steps: ['Start logging with the vehicle safely parked.', 'Let the engine idle steadily for 10-20 seconds.', 'Mark the midpoint of the quiet idle period.'],
    expected: 'Stable signals should remain flat while counters continue changing.',
    markHint: 'Mark the middle of the steady idle segment.',
    notes: 'Idle captures are useful as a baseline before brake, steering, or throttle tests.',
    futureUse: 'Useful as a baseline segment for future AI and scenario comparison.'
  },
  {
    id: 'throttle_20',
    title: '20% throttle',
    shortLabel: '20% throttle',
    scenarioTag: 'flat_ground_20_throttle',
    eventType: 'throttle',
    markerLabel: '20% throttle input',
    steps: ['Capture a stable idle baseline first.', 'Apply a smooth light throttle input around 20%.', 'Hold briefly, release, then mark the start of the input.'],
    expected: 'Throttle, RPM, or load signals should rise after the event, then settle.',
    markHint: 'Mark when throttle input begins, not when RPM peaks.',
    notes: 'Use repeated light inputs to separate throttle/RPM from counters and noisy bytes.',
    futureUse: 'Useful for mapping throttle, RPM, load, and response lag candidates.'
  },
  {
    id: 'door',
    title: 'Door open/close',
    shortLabel: 'Door',
    scenarioTag: 'door_test',
    eventType: 'door',
    markerLabel: 'Door open',
    steps: ['Start logging with ignition/accessory state stable.', 'Open and close one door with clear pauses.', 'Mark the first door-open moment.'],
    expected: 'Door state bytes should toggle near the marker and stay stable between actions.',
    markHint: 'Mark when the latch first opens.',
    notes: 'Door signals are usually flags or packed body-control bytes.',
    futureUse: 'Useful for body-signal comparison and future scenario libraries.'
  },
  {
    id: 'custom',
    title: 'Custom',
    shortLabel: 'Custom',
    scenarioTag: 'custom',
    eventType: 'custom',
    markerLabel: 'Custom event',
    steps: ['Choose one repeatable action.', 'Capture a quiet baseline first.', 'Mark the exact moment the action starts.'],
    expected: 'Bytes related to the action should change near the event marker more than the baseline.',
    markHint: 'Mark the first visible or physical start of the action.',
    notes: 'Correlation is a discovery aid. Repeat the scenario to validate the candidate signal.',
    futureUse: 'Useful for future community scenario sharing once persistence is added.'
  }
];

type ScenarioSelectorProps = {
  selectedScenarioId: ScenarioPreset['id'];
  onSelectScenario: (scenario: ScenarioPreset) => void;
  onAddSuggestedMarker: (scenario: ScenarioPreset) => void;
};

export function ScenarioSelector({ selectedScenarioId, onSelectScenario, onAddSuggestedMarker }: ScenarioSelectorProps) {
  const selectedScenario = SCENARIOS.find((scenario) => scenario.id === selectedScenarioId) ?? SCENARIOS[0];

  return (
    <article className="scenario-selector-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Step 1: Choose a scenario</p>
          <h3>Scenario Library</h3>
          <p className="panel-note">Pick a repeatable capture workflow, then add its suggested marker before running correlation.</p>
        </div>
        <button className="button primary" type="button" onClick={() => onAddSuggestedMarker(selectedScenario)}>
          Add suggested marker
        </button>
      </div>

      <div className="scenario-option-row" role="list" aria-label="Signal discovery scenarios">
        {SCENARIOS.map((scenario) => {
          const isActive = scenario.id === selectedScenarioId;
          return (
            <button
              className={isActive ? 'scenario-option active' : 'scenario-option'}
              key={scenario.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => onSelectScenario(scenario)}
            >
              <strong>
                {scenario.title}
                {isActive && <span className="scenario-selected-badge" aria-hidden="true">Selected ✓</span>}
              </strong>
              <span>{scenario.shortLabel} / {scenario.markerLabel}</span>
            </button>
          );
        })}
      </div>

      <div className="scenario-detail-grid">
        <div>
          <h4>Capture steps</h4>
          <ol>
            {selectedScenario.steps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
        <div>
          <h4>Expected behavior</h4>
          <p>{selectedScenario.expected}</p>
          <p><strong>Where to mark:</strong> {selectedScenario.markHint}</p>
          <p className="panel-note">{selectedScenario.notes}</p>
        </div>
        <div className="scenario-library-meta">
          <h4>Session tag</h4>
          <p><code>{selectedScenario.scenarioTag}</code></p>
          <p className="panel-note">{selectedScenario.futureUse}</p>
        </div>
      </div>
    </article>
  );
}
